import {
	NotificationAudience,
	NotificationCategory,
	useGetNotificationSettingsQuery,
	useSendTestNotificationMutation,
	useSetNotificationSettingMutation,
	type GetNotificationSettingsQuery,
} from '@codezo/graphql';
import { Eye, Lock, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { FilterChip } from '../../components/ui/filter-chips';
import { Switch } from '../../components/ui/switch';
import { cn } from '../../lib/utils';
import { EmailTemplatePreviewDialog } from './EmailTemplatePreviewDialog';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '../../components/ui/tooltip';

type NotificationSetting = GetNotificationSettingsQuery['getNotificationSettings'][number];
type Channel = 'email' | 'bell';

/**
 * Section headings and audience chips are the only copy this file owns — both
 * are names for a fixed enum, not wording about what a notification does. Every
 * sentence on the screen (label, description) comes from the backend, so new
 * notification types appear here without a frontend release.
 *
 * Categories are deliberately *partial*: `humanize` turns PAYMENTS into
 * "Payments" perfectly well, so only the ones where that isn't good enough are
 * listed. A new category then names itself instead of requiring a release for a
 * label that would have been identical. Audiences below are exhaustive for the
 * opposite reason.
 */
const CATEGORY_LABELS: Partial<Record<NotificationCategory, string>> = {
	[NotificationCategory.Account]: 'Account & security',
};

/**
 * Display order. Anything not listed sorts after these but above the trailing
 * groups — a new category appearing below "Other" would read as a bug.
 */
const CATEGORY_ORDER: NotificationCategory[] = [
	NotificationCategory.Joining,
	NotificationCategory.Offers,
	NotificationCategory.Orders,
	NotificationCategory.Samples,
	NotificationCategory.Payments,
];

/**
 * Pinned to the bottom, in this order.
 *
 * `OTHER` because a catch-all belongs last. `ACCOUNT` because every row in it is
 * always-on — password changed, new device, account locked — so the whole
 * section is read-only. Leading with six switches nobody can move buried the
 * ones an admin actually came to change.
 */
const TRAILING_CATEGORIES: string[] = [NotificationCategory.Other, NotificationCategory.Account];

/**
 * Who receives the notification. Exhaustive on purpose, unlike categories: these
 * labels are editorial rather than mechanical — ADMIN reads as "Staff", USER as
 * "Account holder" — and each carries a colour with meaning. A new audience that
 * quietly named itself "Vetter" and rendered grey would be worse than a build
 * failure asking someone to decide.
 */
const AUDIENCE_LABELS: Record<NotificationAudience, string> = {
	[NotificationAudience.Admin]: 'Staff',
	[NotificationAudience.Client]: 'Client',
	[NotificationAudience.Guest]: 'Guest',
	[NotificationAudience.User]: 'Account holder',
};

const AUDIENCE_HINTS: Record<NotificationAudience, string> = {
	[NotificationAudience.Admin]: '05 Creation staff.',
	[NotificationAudience.Client]: 'Users at a client organisation.',
	[NotificationAudience.Guest]:
		'Someone with no account — an invitee, an applicant, or a guest on an offer link.',
	[NotificationAudience.User]: 'The account holder, whoever they are.',
};

const AUDIENCE_ORDER: NotificationAudience[] = [
	NotificationAudience.Client,
	NotificationAudience.Admin,
	NotificationAudience.Guest,
	NotificationAudience.User,
];

/** The two dimensions the list can be cut by. */
type Dimension = 'category' | 'audience';

const otherDimension = (dimension: Dimension): Dimension =>
	dimension === 'category' ? 'audience' : 'category';

/** A value this build has never seen still gets a readable heading. */
function humanize(value: string): string {
	return value
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/^./, (c) => c.toUpperCase());
}

function groupHeading(dimension: Dimension, key: string): string {
	const labels: Partial<Record<string, string>> =
		dimension === 'category' ? CATEGORY_LABELS : AUDIENCE_LABELS;
	return labels[key] ?? humanize(key);
}

type Group = { key: string; rows: NotificationSetting[] };

/**
 * Groups on one dimension: listed values in their fixed order, then anything
 * this build doesn't recognise, then the trailing groups. Unknown values are
 * sorted rather than dropped — new categories are added regularly, and the
 * failure to avoid is one that hides a group.
 */
function groupOn(settings: readonly NotificationSetting[], dimension: Dimension): Group[] {
	const order: string[] = dimension === 'category' ? CATEGORY_ORDER : AUDIENCE_ORDER;
	// Audiences have no trailing group; every one of them is equally actionable.
	const trailing: string[] = dimension === 'category' ? TRAILING_CATEGORIES : [];
	const groups = new Map<string, NotificationSetting[]>();

	for (const setting of settings) {
		const key = dimension === 'category' ? setting.category : setting.audience;
		const existing = groups.get(key);
		if (existing) existing.push(setting);
		else groups.set(key, [setting]);
	}

	const present = [...groups.keys()];
	const known = order.filter((key) => groups.has(key));
	const pinned = trailing.filter((key) => groups.has(key));
	const unknown = present.filter((key) => !order.includes(key) && !trailing.includes(key));

	return [...known, ...unknown, ...pinned].map((key) => ({
		key,
		rows: groups.get(key) ?? [],
	}));
}

/**
 * Both dimensions at once: the chosen one as sections, the other as bands
 * within each. Neither answers the question alone — "who receives this" and
 * "what is it about" are both worth seeing, and picking a primary shouldn't
 * cost you the secondary.
 */
function groupSettings(
	settings: readonly NotificationSetting[],
	primary: Dimension
): { key: string; rows: NotificationSetting[]; bands: Group[] }[] {
	return groupOn(settings, primary).map((group) => ({
		...group,
		bands: groupOn(group.rows, otherDimension(primary)),
	}));
}

/* ── Column headers, repeated per card so the switches stay labelled ── */

function ChannelHeaders() {
	return (
		<div className="flex items-center gap-6">
			<span className="w-12 text-center text-xs font-medium uppercase tracking-[0.06em] text-text-4">
				Email
			</span>
			<span className="w-12 text-center text-xs font-medium uppercase tracking-[0.06em] text-text-4">
				In app
			</span>
		</div>
	);
}

/* ── Who receives it ── */

/**
 * Audience gets a colour of its own wherever it appears. Whether something goes
 * to a client or to 05 staff changes what switching it off means, so it has to
 * survive a glance down a long list — a grey micro-label doesn't.
 */
const AUDIENCE_TONES: Record<NotificationAudience, string> = {
	[NotificationAudience.Client]: 'bg-accent-soft text-accent',
	[NotificationAudience.Admin]: 'bg-status-info-soft text-status-info',
	[NotificationAudience.Guest]: 'bg-status-warn-soft text-status-warn',
	[NotificationAudience.User]: 'bg-status-neutral-soft text-text-2',
};

function AudienceTag({ audience }: { audience: NotificationAudience }) {
	const label = groupHeading('audience', audience);
	const hint = AUDIENCE_HINTS[audience];

	const tag = (
		<Badge
			className={cn(
				'tracking-[0.04em] uppercase',
				AUDIENCE_TONES[audience] ?? 'bg-status-neutral-soft text-text-2'
			)}
		>
			{label}
		</Badge>
	);

	if (!hint) return tag;

	return (
		<TooltipProvider delay={200}>
			<Tooltip>
				<TooltipTrigger render={<span className="flex w-fit" tabIndex={0} />}>
					{tag}
				</TooltipTrigger>
				<TooltipContent>{hint}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

/** "Sent to <Client>" for audiences; a plain label for categories. */
function GroupLabel({ dimension, groupKey }: { dimension: Dimension; groupKey: string }) {
	if (dimension !== 'audience') {
		return (
			<span className="text-xs font-medium tracking-[0.06em] text-text-3 uppercase">
				{groupKey === '' ? '—' : groupHeading(dimension, groupKey)}
			</span>
		);
	}

	return (
		<span className="flex items-center gap-2">
			{/* Without this the bare audience name reads as another category. */}
			<span className="text-xs font-medium tracking-[0.06em] text-text-4 uppercase">
				Sent to
			</span>
			<AudienceTag audience={groupKey as NotificationAudience} />
		</span>
	);
}

/* ── The secondary dimension, as a band inside a section ── */

function BandHeading({
	dimension,
	groupKey,
	first,
}: {
	dimension: Dimension;
	groupKey: string;
	/** The first band butts against the card header, which already has a rule. */
	first: boolean;
}) {
	return (
		<div
			className={cn(
				// No fill: the coloured tag is the anchor now, and a tinted band
				// behind it turned the card into stripes. (An earlier version used
				// bg-1, which in light mode is the same white as the card — the
				// band didn't exist at all.)
				// No rule under it either — the heading belongs to the rows beneath
				// it, and a line there would cut it off from them.
				'px-6 pt-4 pb-2',
				// The last row of the previous band drops its own rule, so without
				// this the two bands would run together without a separator.
				!first && 'border-t border-line-1'
			)}
		>
			<GroupLabel dimension={dimension} groupKey={groupKey} />
		</div>
	);
}

/* ── One notification type ── */

function SettingRow({
	setting,
	pendingChannel,
	onToggle,
	onPreview,
}: {
	setting: NotificationSetting;
	pendingChannel: Channel | null;
	onToggle: (setting: NotificationSetting, channel: Channel, next: boolean) => void;
	onPreview: (templateKey: string) => void;
}) {
	const locked = setting.alwaysOn;

	const renderSwitch = (channel: Channel, checked: boolean) => (
		<div className="flex w-12 justify-center">
			<Switch
				checked={checked}
				disabled={locked || pendingChannel !== null}
				onCheckedChange={(next) => onToggle(setting, channel, next)}
				aria-label={`${channel === 'email' ? 'Email' : 'In app'} — ${setting.label}`}
			/>
		</div>
	);

	return (
		<div className="flex items-center justify-between gap-6 px-6 py-4 border-b border-line-1 last:border-b-0">
			<div className="min-w-0">
				<div className="flex items-center gap-1.5">
					<p className="text-base font-medium text-text-1">{setting.label}</p>
					{locked && (
						<TooltipProvider delay={200}>
							<Tooltip>
								<TooltipTrigger
									render={
										<span
											className="flex items-center text-text-4"
											tabIndex={0}
										/>
									}
								>
									<Lock size={14} />
								</TooltipTrigger>
								{/*
								 * Deliberately not "security": a type is locked either
								 * because it's a security notice or because it's how the
								 * recipient gets their access link, and nothing in the
								 * response says which. The row's own description does.
								 */}
								<TooltipContent>
									Always on — this one can&apos;t be switched off.
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}
				</div>
				<p className="mt-0.5 text-sm text-text-3">{setting.description}</p>
				{/*
				 * Deciding whether to switch an email off usually means reading it
				 * first. Empty only for a type with no metadata entry yet.
				 */}
				{setting.templateKey && (
					<button
						onClick={() => onPreview(setting.templateKey)}
						className="mt-1.5 flex cursor-pointer items-center gap-1 text-xs text-text-4 transition-colors hover:text-text-1"
					>
						<Eye size={14} />
						Preview email
					</button>
				)}
			</div>
			<div className="flex shrink-0 items-center gap-6">
				{renderSwitch('email', setting.emailEnabled)}
				{renderSwitch('bell', setting.bellEnabled)}
			</div>
		</div>
	);
}

/* ════════════════════════ NOTIFICATION SETTINGS TAB ════════════════════════ */

export function NotificationSettingsTab() {
	const { data, loading } = useGetNotificationSettingsQuery({ fetchPolicy: 'no-cache' });
	const [setNotificationSetting] = useSetNotificationSettingMutation();
	const [sendTestNotification, { loading: sendingTest }] = useSendTestNotificationMutation();

	const [settings, setSettings] = useState<NotificationSetting[]>([]);
	/** `${type}:${channel}` for the switch currently awaiting a response. */
	const [pending, setPending] = useState<string | null>(null);
	const [previewKey, setPreviewKey] = useState<string | null>(null);
	const [groupBy, setGroupBy] = useState<Dimension>('category');

	useEffect(() => {
		if (data?.getNotificationSettings) setSettings([...data.getNotificationSettings]);
	}, [data]);

	/**
	 * Never flips optimistically: the backend rejects always-on types with a
	 * reason, and that reason is what the admin needs to see.
	 */
	const handleToggle = async (setting: NotificationSetting, channel: Channel, next: boolean) => {
		const key = `${setting.type}:${channel}`;
		setPending(key);

		try {
			const result = await setNotificationSetting({
				variables: {
					input: {
						type: setting.type,
						...(channel === 'email' ? { emailEnabled: next } : { bellEnabled: next }),
					},
				},
			});
			const response = result.data?.setNotificationSetting;

			if (!response?.success) {
				toast.error(response?.message || 'This notification could not be updated');
				return;
			}

			setSettings((prev) =>
				prev.map((row) =>
					row.type === setting.type
						? channel === 'email'
							? { ...row, emailEnabled: next }
							: { ...row, bellEnabled: next }
						: row
				)
			);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : 'This notification could not be updated'
			);
		} finally {
			setPending(null);
		}
	};

	/**
	 * No arguments: bell-only, on a type that can't be switched off. That makes
	 * the result unambiguous — if nothing lands in the bell, the delivery path is
	 * broken, not merely disabled. Surface the backend's own message either way,
	 * since a refusal explains itself better than we could.
	 */
	const handleSendTest = async () => {
		try {
			const result = await sendTestNotification();
			const response = result.data?.sendTestNotification;
			if (response?.success) {
				toast.success(response.message || 'Test notification sent');
			} else {
				toast.error(response?.message || 'The test notification was not sent');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'The test notification was not sent');
		}
	};

	if (loading) {
		return (
			<div className="flex flex-col gap-6">
				{Array.from({ length: 3 }).map((_, i) => (
					<Card key={i} className="border-line-1 bg-surface gap-0 py-0">
						<div className="space-y-3 p-6">
							<div className="h-5 w-40 animate-pulse rounded bg-surface-fill" />
							<div className="h-10 animate-pulse rounded bg-surface-fill" />
							<div className="h-10 animate-pulse rounded bg-surface-fill" />
						</div>
					</Card>
				))}
			</div>
		);
	}

	const groups = groupSettings(settings, groupBy);

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="text-base text-text-3">
					Platform-wide switches, one per notification type. These apply to everyone —
					recipients cannot turn notifications off for themselves.
				</p>
				<div className="flex shrink-0 flex-wrap items-center gap-3">
					<div className="flex items-center gap-1.5">
						<span className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
							Group by
						</span>
						<FilterChip
							active={groupBy === 'category'}
							onClick={() => setGroupBy('category')}
						>
							Category
						</FilterChip>
						<FilterChip
							active={groupBy === 'audience'}
							onClick={() => setGroupBy('audience')}
						>
							Audience
						</FilterChip>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={handleSendTest}
						disabled={sendingTest}
						title="Raises a real notification at your own bell, on a type that can't be switched off"
					>
						<Send size={14} data-icon="inline-start" />
						{sendingTest ? 'Sending…' : 'Send test'}
					</Button>
				</div>
			</div>

			{groups.map(({ key, rows, bands }) => (
				<Card key={key} className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="flex items-center justify-between gap-6 border-b border-line-1 px-6 py-3">
						<h3 className="flex items-center gap-2 text-sm font-medium tracking-[0.04em] text-text-1 uppercase">
							{groupBy === 'audience' ? (
								// Same tag as the bands use, so an audience looks like
								// itself whether it's the section or the band.
								<AudienceTag audience={key as NotificationAudience} />
							) : (
								groupHeading(groupBy, key)
							)}
							<span className="text-xs font-normal tracking-normal text-text-4 normal-case">
								{rows.length}
							</span>
						</h3>
						<ChannelHeaders />
					</div>
					{bands.map((band, bandIndex) => (
						<div key={band.key}>
							{/*
							 * Suppressed when the section has only one band — a
							 * sub-heading that repeats for every row underneath it
							 * is chrome, not information.
							 */}
							{bands.length > 1 && (
								<BandHeading
									dimension={otherDimension(groupBy)}
									groupKey={band.key}
									first={bandIndex === 0}
								/>
							)}
							{band.rows.map((setting) => (
								<SettingRow
									key={setting.type}
									setting={setting}
									pendingChannel={
										pending === `${setting.type}:email`
											? 'email'
											: pending === `${setting.type}:bell`
												? 'bell'
												: null
									}
									onToggle={handleToggle}
									onPreview={setPreviewKey}
								/>
							))}
						</div>
					))}
				</Card>
			))}

			<EmailTemplatePreviewDialog
				templateKey={previewKey}
				onClose={() => setPreviewKey(null)}
			/>
		</div>
	);
}
